/**
 * Library match card — the "inline ranked list" the researcher picks from
 * when a saved library variable may already cover what they asked for.
 *
 * Same chassis as the other chat object cards ({@link AssetCard}): white
 * card, 3px brand-primary left rail, kind icon + kind label header. Body is
 * the ranked candidate list — the best match tinted, alternatives as plain
 * outlined rows — each with a Use action and a "View details" link into the
 * variable detail record, plus a "None fit? / + Build new" footer. No status
 * or "Best match" chips (design cleanup after the initial 1a mockup).
 *
 * The choice is sent back as a plain user message (with the checkpointId);
 * the orchestrator reads the researcher's pick from that text.
 */

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import FunctionsIcon from '@mui/icons-material/Functions';
import type { SxProps, Theme } from '@mui/material';
import { alpha, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { borderRadius, opacity, shadows } from '@verantos-dev/shared-ui-components/tokens';

import { Markdown } from './Markdown';
import { useAvSlugClick } from '../context/AvSlugClickContext';
import type {
  AvSelectionCandidate,
  AvSelectionCheckpoint,
  EpiChatSendPayload,
} from '../types/epi-chat-types';

/** Shared chassis frame (matches AssetCard's object-card styling). */
const cardFrameSx: SxProps<Theme> = {
  padding: 4,
  backgroundColor: 'background.paper',
  borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
  borderRadius: `${borderRadius.sm}px`,
  boxShadow: shadows.sm,
};

/**
 * Which of the five headline sentences applies — derived from whether a
 * `primary` was surfaced and how many `alternatives` accompany it. Kept as
 * flat per-case i18n messages (not one nested ICU `select`/`plural`) so each
 * sentence stays a plain, independently translatable string.
 */
type LibraryMatchHeadlineCase =
  | 'primaryOnly'
  | 'primaryAndOne'
  | 'primaryAndMany'
  | 'similarOne'
  | 'similarMany';

// Exported for direct unit testing — see getLibraryMatchHeadlineCase.spec.ts.
export const getLibraryMatchHeadlineCase = (
  hasPrimary: boolean,
  otherCount: number
): LibraryMatchHeadlineCase => {
  if (hasPrimary) {
    if (otherCount === 0) return 'primaryOnly';
    return otherCount === 1 ? 'primaryAndOne' : 'primaryAndMany';
  }
  return otherCount === 1 ? 'similarOne' : 'similarMany';
};

const LIBRARY_MATCH_HEADLINE_MESSAGES: Record<
  LibraryMatchHeadlineCase,
  { id: string; defaultMessage: string }
> = {
  primaryOnly: {
    id: 'epiChat.libraryMatch.headline.primaryOnly',
    defaultMessage: 'Found a close match for <strong>{name}</strong>',
  },
  primaryAndOne: {
    id: 'epiChat.libraryMatch.headline.primaryAndOne',
    defaultMessage: 'Found a close match for <strong>{name}</strong>, and 1 other similar variable',
  },
  primaryAndMany: {
    id: 'epiChat.libraryMatch.headline.primaryAndMany',
    defaultMessage:
      'Found a close match for <strong>{name}</strong>, and {otherCount} other similar variables',
  },
  similarOne: {
    id: 'epiChat.libraryMatch.headline.similarOne',
    defaultMessage: 'Found a variable similar to <strong>{name}</strong>',
  },
  similarMany: {
    id: 'epiChat.libraryMatch.headline.similarMany',
    defaultMessage: 'Found {otherCount} variables similar to <strong>{name}</strong>',
  },
};

interface CandidateCardProps {
  candidate: AvSelectionCandidate;
  isBestMatch: boolean;
  onUse: () => void;
  onViewDetails: (() => void) | null;
  disabled?: boolean;
  /**
   * Reference card: the researcher is choosing which variable to follow as a
   * pattern, not which one to adopt — so the action reads "Follow".
   */
  isReference?: boolean;
}

const CandidateCard = ({
  candidate,
  isBestMatch,
  onUse,
  onViewDetails,
  disabled,
  isReference,
}: CandidateCardProps) => {
  const intl = useIntl();
  return (
    <Stack
      spacing={1}
      data-testid={`library-match-candidate-${candidate.analyticalVariableId}`}
      sx={{
        padding: 3,
        borderRadius: `${borderRadius.sm}px`,
        border: (theme) =>
          `1px solid ${isBestMatch ? theme.palette.primary.main : theme.palette.divider}`,
        backgroundColor: (theme) =>
          isBestMatch ? alpha(theme.palette.primary.main, opacity[8]) : 'background.paper',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>
        {candidate.dataElement}
      </Typography>
      {candidate.reason && (
        <Typography variant="body2" color="text.secondary">
          {candidate.reason}
        </Typography>
      )}
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant={isBestMatch ? 'contained' : 'text'}
          color="primary"
          size="small"
          startIcon={isBestMatch ? <CheckCircleOutlineRoundedIcon /> : undefined}
          onClick={onUse}
          disabled={disabled}
          aria-label={
            isReference ? `Follow ${candidate.dataElement}` : `Use ${candidate.dataElement}`
          }
          data-testid={`library-match-use-${candidate.analyticalVariableId}`}
          // Design 1a: the contained best-match button is uppercase ("USE THIS");
          // the alternatives' Use is a sentence-case text link.
          sx={isBestMatch ? undefined : { textTransform: 'none' }}
        >
          {isReference
            ? isBestMatch
              ? intl.formatMessage({
                  id: 'epiChat.libraryMatch.followThis',
                  defaultMessage: 'Follow this',
                })
              : intl.formatMessage({
                  id: 'epiChat.libraryMatch.follow',
                  defaultMessage: 'Follow',
                })
            : isBestMatch
            ? intl.formatMessage({
                id: 'epiChat.libraryMatch.useThis',
                defaultMessage: 'Use this',
              })
            : intl.formatMessage({ id: 'epiChat.libraryMatch.use', defaultMessage: 'Use' })}
        </Button>
        {onViewDetails && (
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={onViewDetails}
            disabled={disabled}
            aria-label={`View details of ${candidate.dataElement}`}
            data-testid={`library-match-details-${candidate.analyticalVariableId}`}
            sx={{ textTransform: 'none' }}
          >
            {intl.formatMessage({
              id: 'epiChat.libraryMatch.viewDetails',
              defaultMessage: 'View details',
            })}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

interface LibraryMatchCardProps {
  checkpoint: AvSelectionCheckpoint;
  onAnswer: (answer: EpiChatSendPayload) => void;
  disabled?: boolean;
}

export const LibraryMatchCard = ({ checkpoint, onAnswer, disabled }: LibraryMatchCardProps) => {
  const intl = useIntl();
  const avSlugClick = useAvSlugClick();

  const isReference = checkpoint.purpose === 'search_reference_variable';

  // Which headline sentence to show — a reference question, or the
  // primary/alternatives-aware wording. Memoized so the branch is computed
  // once per relevant change, not re-evaluated inline in the JSX below.
  const headlineMessage = useMemo(() => {
    if (isReference) {
      return {
        id: 'epiChat.libraryMatch.referenceHeadlineSimple',
        defaultMessage: 'Which variable to follow?',
      };
    }
    return LIBRARY_MATCH_HEADLINE_MESSAGES[
      getLibraryMatchHeadlineCase(checkpoint.primary !== null, checkpoint.alternatives.length)
    ];
  }, [isReference, checkpoint.primary, checkpoint.alternatives.length]);

  // Belt and braces: `allowBuildNew` is the backend's mechanism, `purpose` is the
  // intent. Gating on both means a caller that forgets the flag still can't put
  // "Build new" on a reference question — where clicking it can only mean
  // "carry on", which is what confused the researcher who reported this.
  const showBuildNew = checkpoint.allowBuildNew && !isReference;

  // Reply contract — the orchestrator reads the
  // researcher's pick from this message text + checkpointId. A reference pick
  // says something different on purpose: "I'll use X" is indistinguishable
  // between adopting X and following X's shape, and the orchestrator's next
  // action differs between the two.
  const sendSelection = (candidate: AvSelectionCandidate) => {
    onAnswer({
      text: isReference
        ? `Follow "${candidate.dataElement}" (alias: ${candidate.name}) as the pattern.`
        : `I'll use "${candidate.dataElement}" (alias: ${candidate.name}).`,
      checkpointId: checkpoint.checkpointId,
    });
  };
  const sendBuildNew = () => {
    onAnswer({
      text: 'Build new instead — no variable match selected.',
      checkpointId: checkpoint.checkpointId,
    });
  };

  const header = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <FunctionsIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} aria-hidden />
      <Typography variant="body2" color="text.secondary" noWrap>
        {isReference
          ? intl.formatMessage({
              id: 'epiChat.libraryMatch.referenceKindLabel',
              defaultMessage: 'Pattern to follow',
            })
          : intl.formatMessage({
              id: 'epiChat.libraryMatch.kindLabel',
              defaultMessage: 'Variable match',
            })}
      </Typography>
    </Stack>
  );

  if (checkpoint.answered) {
    // The reply text was authored by this card (sendSelection), so the chosen
    // candidate is recoverable from the quoted display name — render the
    // variable name instead of echoing the reply verbatim (the reply already
    // renders as the user's own bubble right below the card). Build-new and
    // free-typed replies have no matching candidate and fall back to the
    // answer text.
    const chosen = [checkpoint.primary, ...checkpoint.alternatives]
      .filter((c): c is AvSelectionCandidate => c !== null)
      .find((c) => checkpoint.answerText?.includes(`"${c.dataElement}"`));
    return (
      <Stack spacing={2} data-testid="library-match-card-resolved" sx={cardFrameSx}>
        {header}
        {chosen ? (
          <>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {chosen.dataElement}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isReference
                ? intl.formatMessage({
                    id: 'epiChat.libraryMatch.followedAsPattern',
                    defaultMessage: 'Followed as pattern',
                  })
                : intl.formatMessage({
                    id: 'epiChat.libraryMatch.reusedFromLibrary',
                    defaultMessage: 'Reused from library',
                  })}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {checkpoint.answerText}
          </Typography>
        )}
      </Stack>
    );
  }

  const candidates: AvSelectionCandidate[] = [
    ...(checkpoint.primary ? [checkpoint.primary] : []),
    ...checkpoint.alternatives,
  ];

  return (
    <Stack spacing={2} data-testid="library-match-card" sx={cardFrameSx}>
      {header}

      {checkpoint.requestedVariable ? (
        <Typography variant="body2">
          {intl.formatMessage(headlineMessage, {
            count: candidates.length,
            name: checkpoint.requestedVariable,
            otherCount: checkpoint.alternatives.length,
            strong: (chunks) => (
              <Box component="strong" sx={{ fontWeight: 600 }}>
                {chunks}
              </Box>
            ),
          })}
        </Typography>
      ) : (
        // Pre-OR-6338 payloads (replayed sessions) carry no requestedVariable
        // — fall back to the sub-agent's summary prose so the card still
        // explains itself.
        checkpoint.summary && <Markdown dense>{checkpoint.summary}</Markdown>
      )}

      {candidates.length > 0 && (
        <Stack spacing={2}>
          {candidates.map((candidate, index) => (
            <CandidateCard
              key={candidate.analyticalVariableId}
              candidate={candidate}
              isBestMatch={index === 0 && checkpoint.primary !== null}
              onUse={() => sendSelection(candidate)}
              onViewDetails={avSlugClick ? () => avSlugClick(candidate.analyticalVariableId) : null}
              disabled={disabled}
              isReference={isReference}
            />
          ))}
        </Stack>
      )}

      {showBuildNew && (
        <>
          {candidates.length > 0 && <Divider />}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {intl.formatMessage({
                id: 'epiChat.libraryMatch.noneFit',
                defaultMessage: 'None fit?',
              })}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={sendBuildNew}
              disabled={disabled}
              data-testid="library-match-build-new"
              // Design polish: sentence case — renders "+ Build new".
              sx={{ textTransform: 'none' }}
            >
              {intl.formatMessage({
                id: 'epiChat.libraryMatch.buildNew',
                defaultMessage: 'Build new',
              })}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};
